import type { WorkingHours } from "@ai-admin/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { queryClient, trpc, trpcErrorCode, trpcErrorDetails } from "../../app/trpc.js";
import { Button, Card, Chip, FormRow, focusRing, Input, Label, Notice, PageTitle, SectionTitle, Spinner, Toggle } from "../../ui/index.js";
import { addDays, dayKey, dayStart, fmtDateTime, useCabinet } from "../cabinet/index.js";
import { invalidateBookings } from "./invalidate.js";
import { TimeOffSheet } from "./TimeOffSheet.js";

/** Понедельник первым; в базе 0 это воскресенье. */
const WEEK = [1, 2, 3, 4, 5, 6, 0] as const;
const hh = (s: string) => s.slice(0, 5);

/**
 * Мастер: имя, услуги, выключение (владелец), график по дням недели (несколько интервалов в день),
 * отгулы и больничные. Мастер открывает здесь свой график.
 * На широком экране две колонки: слева карточка мастера и отгулы, справа график.
 */
export function MasterPage() {
  const { t, i18n } = useTranslation();
  const { masterId = "" } = useParams();
  const navigate = useNavigate();
  const cab = useCabinet();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const masters = useQuery(trpc.calendar.masters.list.queryOptions({ salonId: cab.salonId, includeInactive: true }, { enabled: cab.isOwner }));
  const services = useQuery(trpc.calendar.services.list.queryOptions({ salonId: cab.salonId }, { enabled: cab.isOwner }));
  const hours = useQuery(trpc.calendar.workingHours.get.queryOptions({ masterId }));
  const range = useMemo(() => {
    const today = dayKey(new Date(), cab.timezone);
    return { from: dayStart(today, cab.timezone), to: dayStart(addDays(today, 90), cab.timezone) };
  }, [cab.timezone]);
  const offs = useQuery(trpc.calendar.timeOff.list.queryOptions({ masterId, ...range }));
  const master = masters.data?.find((m) => m.id === masterId);

  const [name, setName] = useState("");
  const [active, setActive] = useState(true);
  const [serviceIds, setServiceIds] = useState<string[]>([]);
  const [grid, setGrid] = useState<WorkingHours[] | null>(null);
  const [offOpen, setOffOpen] = useState(false);

  useEffect(() => {
    if (master) {
      setName(master.name);
      setActive(master.active);
      setServiceIds(master.serviceIds);
    }
  }, [master]);
  useEffect(() => {
    if (hours.data && grid === null) setGrid(hours.data.map((h) => ({ ...h, start: hh(h.start), end: hh(h.end) })));
  }, [hours.data, grid]);

  const saveMaster = useMutation(
    trpc.calendar.masters.save.mutationOptions({ onSuccess: () => queryClient.invalidateQueries(trpc.calendar.masters.pathFilter()) }),
  );
  const remove = useMutation(
    trpc.calendar.masters.remove.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.calendar.masters.pathFilter());
        // Пользователь кабинета мог быть привязан к удалённому мастеру, а контекст кэшируется на 5 минут.
        void queryClient.invalidateQueries(trpc.cabinet.context.pathFilter());
        navigate(`${cab.base}/settings/masters`);
      },
    }),
  );
  const saveHours = useMutation(
    trpc.calendar.workingHours.set.mutationOptions({
      onSuccess: (data) => queryClient.setQueryData(trpc.calendar.workingHours.get.queryKey({ masterId }), data),
    }),
  );
  const delOff = useMutation(
    trpc.calendar.timeOff.delete.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.calendar.timeOff.pathFilter());
        invalidateBookings();
      },
    }),
  );

  if (hours.isError) return <Notice kind="error">{t("masters.notYours")}</Notice>;
  if (cab.loading || hours.isPending || !grid) return <Spinner />;
  const dayRows = (wd: number) => grid.map((h, i) => ({ h, i })).filter((x) => x.h.weekday === wd);
  const setRow = (i: number, patch: Partial<WorkingHours>) => {
    saveHours.reset();
    setGrid(grid.map((h, j) => (j === i ? { ...h, ...patch } : h)));
  };
  const toggleDay = (wd: number, on: boolean) => {
    saveHours.reset();
    setGrid(on ? [...grid, { weekday: wd, start: "09:00", end: "18:00" }] : grid.filter((h) => h.weekday !== wd));
  };
  const addInterval = (wd: number) => setGrid([...grid, { weekday: wd, start: "14:00", end: "18:00" }]);
  const removeRow = (i: number) => setGrid(grid.filter((_, j) => j !== i));
  const weekdayName = (wd: number) => new Intl.DateTimeFormat(i18n.language, { weekday: "long", timeZone: "UTC" }).format(new Date(Date.UTC(2026, 0, 4 + wd)));
  const gridValid = grid.every((h) => h.start < h.end);

  return (
    <div>
      <PageTitle>{master?.name ?? t("masters.mySchedule")}</PageTitle>

      <div className="lg:grid lg:grid-cols-2 lg:items-start lg:gap-6">
        <div className="lg:col-start-1">
          {cab.isOwner && master && (
            <Card className="space-y-3">
              <FormRow label={t("masters.name")} htmlFor="m-name">
                <Input id="m-name" value={name} onChange={(e) => setName(e.target.value)} />
              </FormRow>
              <div>
                <Label>{t("masters.services")}</Label>
                <div className="flex flex-wrap gap-2">
                  {services.data?.map((s) => {
                    const on = serviceIds.includes(s.id);
                    return (
                      <Chip
                        key={s.id}
                        active={on}
                        onClick={() => setServiceIds((cur) => (cur.includes(s.id) ? cur.filter((x) => x !== s.id) : [...cur, s.id]))}
                      >
                        <span className="inline-flex items-center gap-1.5">
                          <span className={on ? "inline-block w-4" : "inline-block w-4 text-transparent"} aria-hidden="true">
                            ✓
                          </span>
                          {s.name}
                        </span>
                      </Chip>
                    );
                  })}
                </div>
                <p className="mt-1 text-sm text-slate-500">{t("masters.servicesHint")}</p>
              </div>
              <Toggle checked={active} onChange={setActive} label={t("masters.active")} hint={t("masters.activeHint")} />
              {saveMaster.isError && <Notice kind="error">{t("common.error")}</Notice>}
              {saveMaster.isSuccess && <Notice kind="success">{t("settings.saved")}</Notice>}
              <Button
                className="w-full lg:w-auto"
                disabled={!name.trim() || saveMaster.isPending}
                onClick={() => saveMaster.mutate({ id: masterId, salonId: cab.salonId, name: name.trim(), active, serviceIds })}
              >
                {t("settings.save")}
              </Button>
              {confirmDelete ? (
                <div className="space-y-2">
                  <p className="text-sm text-slate-700">{t("masters.deleteAsk", { name: master.name })}</p>
                  {remove.isError && (
                    <Notice kind="error">
                      {trpcErrorCode(remove.error) !== "conflict"
                        ? t("common.error")
                        : trpcErrorDetails(remove.error)?.reason === "linked"
                          ? t("masters.deleteLinked")
                          : t("masters.deleteBlocked")}
                    </Notice>
                  )}
                  <div className="flex gap-2">
                    <Button className="flex-1" disabled={remove.isPending} onClick={() => remove.mutate({ salonId: cab.salonId, masterId })}>
                      {t("common.remove")}
                    </Button>
                    <Button variant="secondary" className="flex-1" onClick={() => setConfirmDelete(false)}>
                      {t("common.cancel")}
                    </Button>
                  </div>
                </div>
              ) : (
                <Button variant="secondary" className="w-full lg:w-auto" onClick={() => setConfirmDelete(true)}>
                  {t("common.remove")}
                </Button>
              )}
            </Card>
          )}
        </div>

        <div className="lg:col-start-2 lg:row-start-1 lg:row-span-2">
          <SectionTitle className={cab.isOwner && master ? "lg:mt-0" : ""}>{t("masters.hours")}</SectionTitle>
          <Card className="space-y-1">
            {WEEK.map((wd) => {
              const rows = dayRows(wd);
              return (
                <div key={wd} className="border-b border-slate-100 py-2 last:border-b-0">
                  <Toggle checked={rows.length > 0} onChange={(v) => toggleDay(wd, v)} label={<span className="capitalize">{weekdayName(wd)}</span>} />
                  {rows.map(({ h, i }) => (
                    <div key={i} className="mt-1 flex items-center gap-2">
                      <Input type="time" step={300} value={h.start} onChange={(e) => setRow(i, { start: e.target.value })} className="py-2" />
                      <span className="text-slate-400">–</span>
                      <Input type="time" step={300} value={h.end} onChange={(e) => setRow(i, { end: e.target.value })} className="py-2" />
                      <button
                        type="button"
                        className={`rounded px-2 text-xl text-slate-400 hover:text-slate-700 ${focusRing}`}
                        aria-label={t("common.remove")}
                        onClick={() => removeRow(i)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  {rows.length > 0 && (
                    <button type="button" className={`mt-1 rounded text-sm text-slate-500 hover:text-slate-900 ${focusRing}`} onClick={() => addInterval(wd)}>
                      + {t("masters.addInterval")}
                    </button>
                  )}
                </div>
              );
            })}
          </Card>
          {!gridValid && <Notice kind="error">{t("masters.badInterval")}</Notice>}
          {saveHours.isError && <Notice kind="error">{t("common.error")}</Notice>}
          {saveHours.isSuccess && <Notice kind="success">{t("settings.saved")}</Notice>}
          <Button className="mt-3 w-full lg:w-auto" disabled={!gridValid || saveHours.isPending} onClick={() => saveHours.mutate({ masterId, hours: grid })}>
            {t("masters.saveHours")}
          </Button>
        </div>

        <div className="lg:col-start-1">
          <SectionTitle>{t("masters.timeOff")}</SectionTitle>
          {offs.data?.length === 0 && <Notice>{t("masters.noTimeOff")}</Notice>}
          <div className="space-y-2">
            {offs.data?.map((o) => (
              <Card key={o.id} className="flex items-center justify-between gap-2">
                <div className="text-sm">
                  <div className="font-medium">{t(`cal.offKind.${o.kind}`)}</div>
                  <div className="text-slate-500">
                    {fmtDateTime(o.period.start, cab.timezone, i18n.language)} – {fmtDateTime(o.period.end, cab.timezone, i18n.language)}
                  </div>
                </div>
                <Button variant="ghost" className="px-3 py-2 text-sm" disabled={delOff.isPending} onClick={() => delOff.mutate({ id: o.id })}>
                  {t("common.remove")}
                </Button>
              </Card>
            ))}
          </div>
          <Button variant="secondary" className="mt-3 w-full lg:w-auto" onClick={() => setOffOpen(true)}>
            {t("cal.timeOff")}
          </Button>
        </div>
      </div>
      <TimeOffSheet open={offOpen} onClose={() => setOffOpen(false)} day={dayKey(new Date(), cab.timezone)} defaultMasterId={masterId} />
    </div>
  );
}
